import { getCompactor } from 'react-grid-layout';

import type { LensCard, LensCardLayout } from '~/types/leLens';

import { CANVAS_COLUMNS, resolveCanvasLayouts } from './canvasLayout';
import { frameRowSpan } from './cards/cardGrid';
import type { LensProposal } from './lensProposal';

type StructureCard = Pick<LensCard, 'id' | 'title' | 'type'> & { layout: LensCardLayout; automatic: boolean };

// LensCanvas passes this exact library compactor to its grid, once per section.
const compactor = getCompactor('vertical');
// LeLensPage's collapsed Rows drawer is 52px tall, projected before section packing.
const collapsedTableRows = frameRowSpan(52);

function renderedCards(cards: Array<StructureCard>): Array<StructureCard> {
  const positions = new Map(
    compactor
      .compact(
        cards.map((card) => ({ i: card.id, ...card.layout })),
        CANVAS_COLUMNS,
      )
      .map((frame) => [frame.i, frame]),
  );
  return cards.map((card) => {
    const { x, y, w, h } = positions.get(card.id)!;
    return { ...card, layout: { ...card.layout, x, y, w, h } };
  });
}

/** Read-only placement uses the board's resolver and final section packing, without results or editor state. */
export function nativeDraftStructure(
  definition: NonNullable<LensProposal['definition']>,
  expandedTableCardIds: ReadonlySet<string> = new Set(),
) {
  const sections = definition.spec.sections ?? [];
  const cards = resolveCanvasLayouts(definition.spec.cards, new Map()).map(
    (card): StructureCard => ({
      id: card.id,
      title: card.title,
      type: card.type,
      layout: {
        ...card.layout!,
        ...(card.type === 'table' && !expandedTableCardIds.has(card.id) ? { h: collapsedTableRows } : {}),
      },
      automatic: !definition.spec.cards.find((source) => source.id === card.id)?.layout,
    }),
  );
  const sourceSection = (id: string) => definition.spec.cards.find((card) => card.id === id)?.section;
  const unassigned = cards.filter((card) => !sections.some((section) => section.id === sourceSection(card.id)));
  return {
    title: definition.title,
    description: definition.description,
    sections: [
      ...(unassigned.length
        ? [{ id: '__unassigned', title: 'Unassigned cards', cards: renderedCards(unassigned) }]
        : []),
      ...sections.map((section) => ({
        id: section.id,
        title: section.title,
        cards: renderedCards(cards.filter((card) => sourceSection(card.id) === section.id)),
      })),
    ],
  };
}
